import { createSlice } from "@reduxjs/toolkit";
import { fetchStudentReports } from "./studentReport.thunk";

const initialState = {
  // Aggregate report data (all students list view)
  studentsSummary: [],
  totalPages: 1,
  currentPage: 1,
  total: 0,
  limit: 20,

  // Single student report data (detailed view)
  selectedStudentProfile: null,
  summaryStats: null, // { total_sessions, total_present, total_absent, attendance_percentage, total_homework, total_tournaments, total_tournament_points, ... }
  sessions: [],       // Session topics and attendance logs
  homework: [],       // Homework assignments and submission logs
  tournaments: [],    // Tournament performance, score, and round-by-round game reviews

  // Meta info
  reportType: null,   // 'all_students_summary' | 'single_student'
  loading: false,
  error: null,
};

const studentReportSlice = createSlice({
  name: "studentReport",
  initialState,

  reducers: {
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },

    setLimit: (state, action) => {
      state.limit = action.payload;
      state.currentPage = 1; // Reset to first page whenever limit changes
    },

    // Clears single student detailed data (e.g., when returning to summary list)
    clearSingleStudentState: (state) => {
      state.selectedStudentProfile = null;
      state.summaryStats = null;
      state.sessions = [];
      state.homework = [];
      state.tournaments = [];
      if (state.reportType === "single_student") {
        state.reportType = null;
      }
      state.error = null;
    },

    // Completely resets the slice to initialState
    resetStudentReportState: () => initialState,

    clearError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      /* ===== FETCH STUDENT REPORTS ===== */
      .addCase(fetchStudentReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStudentReports.fulfilled, (state, action) => {
        state.loading = false;
        state.reportType = action.payload.reportType;

        if (action.payload.reportType === "single_student") {
          // Detailed view for a specific student
          state.selectedStudentProfile = action.payload.student;
          state.summaryStats = action.payload.summary;
          state.sessions = action.payload.data;
          state.homework = action.payload.homework || [];
          state.tournaments = action.payload.tournaments || [];
        } else {
          // Aggregate summary list view for all students
          state.studentsSummary = action.payload.data;
          state.total = action.payload.meta?.total ?? 0;
          state.currentPage = action.payload.meta?.page ?? 1;
          state.totalPages = action.payload.meta?.total_pages ?? 1;
          if (action.payload.meta?.limit) {
            state.limit = action.payload.meta.limit;
          }
          
          // Clear previous single student view data to prevent UI bleed
          state.selectedStudentProfile = null;
          state.summaryStats = null;
          state.sessions = [];
          state.homework = [];
          state.tournaments = [];
        }
      })
      .addCase(fetchStudentReports.rejected, (state, action) => {
        state.loading = false;

        // Do not update error state if the request was intentionally aborted
        if (!action.meta.aborted) {
          state.error = action.payload || "Failed to fetch student reports.";
        }
      });
  },
});

export const {
  setCurrentPage,
  setLimit,
  clearSingleStudentState,
  resetStudentReportState,
  clearError,
} = studentReportSlice.actions;

export default studentReportSlice.reducer;